import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { Button, Link } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { cfPath, ahPath } from '../../../utilities/UrlPaths';
import { formatPhoneNumber } from '../../../utilities/helpers'

export function getBidderColumns({ toggleSingleSalesResult, expandedFlags, setBidderName, setUserRef, setIsOpenSendMessageModal, currencySymbol }) {
  const convertToDigits = (num) => {
    return num.split(",").join("")/100;
  };

  const sortNames = (aFirst, aLast, bFirst, bLast) => {
    const fullNameA = `${aLast} ${aFirst}`.toLowerCase();
    const fullNameB = `${bLast} ${bFirst}`.toLowerCase();
    return fullNameA > fullNameB ? 1 : fullNameA < fullNameB ? -1 : 0;
  };

  const onClickUserEmail = (firstName, lastName, memberRef) => {
    setBidderName(`${firstName} ${lastName}`);
    setUserRef(memberRef);
    setIsOpenSendMessageModal(true);
  };

  const replaceChannelName = (channel) => {
    let correctChannel = channel;
    if (channel.indexOf('Infinite') > -1) {
      correctChannel = 'Private Label';
    }
    return correctChannel;
  };

  return [
    {
      id: 'toggle',
      header: () => '',
      cell: ({ row }) => {
        return (
          <button
            className="btn btn-link"
            type="button"
            onClick={() => toggleSingleSalesResult(row.original.memberRef)}
          >
            <FontAwesomeIcon icon={expandedFlags[row.original.memberRef] ? faAngleUp : faAngleDown} className="fa-lg" />
          </button>
        );
      },
      size: 32,
    },
    {
      accessorKey: 'lastName',
      header: 'Bidder',
      sortUndefined: 'last',
      sortDescFirst: false,
      sortingFn: (rowA, rowB) => {return sortNames(rowA.original.firstName, rowA.original.lastName, rowB.original.firstName, rowB.original.lastName)},
      cell: ({ cell, row }) => {
        return(
          <div style={{ marginLeft: '10px' }}>
              <div>
                <a
                  href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${row.original.lots[0].approvalID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  >
                    {row.original.lastName}, {row.original.firstName} ({replaceChannelName(row.original.channel)})
                 </a>
              </div>
              {row.original.paddleNumber !== '' && (
                <div>Paddle No. {row.original.paddleNumber}</div>
              )}
              <div>{row.original.bidderAddress.street}</div>
              <div>
                {row.original.bidderAddress.city},
                {row.original.bidderAddress.stateID !== '' && (
                  <span> {row.original.bidderAddress.stateID}, </span>
                )}
                <span> {row.original.bidderAddress.postCode}</span>
                <span className="ml-2"> {row.original.bidderAddress.countryID}</span>
              </div>
              {row.original.phone && (
                <div>{formatPhoneNumber(row.original.phone)} (h)</div>
              )}
              {row.original.mobilePhone && (
                <div>{formatPhoneNumber(row.original.mobilePhone)} (m)</div>
              )}

              {row.original.channel?.indexOf('Floor') > -1 ? (
                <div style={{ userSelect: 'all' }}>
                  {row.original.email}
                </div>
              ) : (
                <button
                  className="btn btn-link"
                  onClick={() => onClickUserEmail(row.original.firstName, row.original.lastName, row.original.memberRef)}
                  style={{ padding: '0 0 10px 0', userSelect: 'all' }}
                >
                  {row.original.email}
                </button>
              )}
          </div>
        )
      }
    },
    {
      accessorKey: 'lotsPurchased',
      header: '#Lots Purchased',
      accessorFn: info => info.lotCount,
    },
    {
      accessorKey: 'sumHammer',
      header: 'Hammer Total',
      accessorFn: info => `${currencySymbol || ''}${info.sumHammer}`,
      sortingFn: (rowA, rowB) => {
        return convertToDigits(rowA.original.sumHammer) > convertToDigits(rowB.original.sumHammer) ? 1 : convertToDigits(rowA.original.sumHammer) < convertToDigits(rowB.original.sumHammer) ? -1 : 0
      },
    },
    {
      accessorKey: 'bpTotal',
      header: 'BP Total',
      accessorFn: info => `${currencySymbol || ''}${info.sumBuyPrem}`,
      sortingFn: (rowA, rowB) => {
        return convertToDigits(rowA.original.sumBuyPrem) > convertToDigits(rowB.original.sumBuyPrem) ? 1 : convertToDigits(rowA.original.sumBuyPrem) < convertToDigits(rowB.original.sumBuyPrem) ? -1 : 0
      },
    },
    {
      accessorKey: 'liveFeeTotal',
      header: 'Live Fee Total',
      accessorFn: info => `${currencySymbol || ''}${info.sumLiveFee}`,
      sortingFn: (rowA, rowB) => {
        return convertToDigits(rowA.original.sumLiveFee) > convertToDigits(rowB.original.sumLiveFee) ? 1 : convertToDigits(rowA.original.sumLiveFee) < convertToDigits(rowB.original.sumLiveFee) ? -1 : 0
      },
    },
    {
      accessorKey: 'payableTotal',
      header: 'Payable Total',
      accessorFn: info => `${currencySymbol || ''}${info.sumPayable}`,
      sortingFn: (rowA, rowB) => {
        return convertToDigits(rowA.original.sumPayable) > convertToDigits(rowB.original.sumPayable) ? 1 : convertToDigits(rowA.original.sumPayable) < convertToDigits(rowB.original.sumPayable) ? -1 : 0
      },
    },
  ];
}

export function getLotColumns({ checkboxArray, setCheckboxArray, handleLotCheckbox, handleMemberCheckbox, setFeedbackModalOpen, canAddFeedback, setLoadingFeedbackModalData, handleEditFeedback, handleRemoveFeedback, setCurrentFeedbackLotRef, setIsOpenFeedbackModal, fetchDefaultFeedbackMessages, dispatch, houseRef, multiSession, currencySymbol }) {
  return [
    {
      id: (info) => info.memberRef,
      header: (header) => {
        const memberRef = header.header.memberRef;
        const memberCatalogs = header.header.lots || [];
        const lotArray = memberCatalogs.map(lot => lot.lotRef);
        const allLotsSelected = lotArray.length > 0 && lotArray.every(lotRef => checkboxArray.includes(lotRef));

        return (
          <div className="text-center">
            <input
              type="checkbox"
              id={`member_${memberRef}`}
              data-member={memberRef}
              onChange={handleMemberCheckbox}
              checked={allLotsSelected}
              disabled={header.header.channel.indexOf('Floor') > -1}
            />
          </div>
        )
      },
      cell: ({ row }) =>  {
        return (
          <div style={{ textAlign: 'center' }}>
            {row.original.channel?.indexOf('Floor') > -1 ? (
              <Tooltip
                title="Disputes cannot be opened on lots won by floor bidders"
                style={{ backgroundColor: 'whiteSmoke' }}
                placement="right"
                arrow
              >
                <input
                  type="checkbox"
                  id={`lot_${row.original.lotRef}`}
                  data-member={row.original.memberRef}
                  onChange={(e) => handleLotCheckbox(e)}
                  data-lot={row.original.lotRef}
                  checked={checkboxArray.indexOf(row.original.lotRef) > -1}
                  disabled={true}
                />
                <span>&nbsp;</span>
              </Tooltip>
            ) : (
              <input
                type="checkbox"
                id={`lot_${row.original.lotRef}`}
                data-member={row.original.memberRef}
                onChange={(e) => handleLotCheckbox(e)}
                data-lot={row.original.lotRef}
                checked={checkboxArray.indexOf(row.original.lotRef) > -1}
              />
            )}
          </div>
        )
      },
      size: 34,
    },
    {
      accessorKey: 'title',
      header: 'Lot',
      cell: ({ row }) => {
        return (
          <>
            {multiSession && <span>{`Session ${row.original.catalogPrefix}: `}</span>}
            <a href={row.original.lotUrl} target = "_blank" rel="noopener noreferrer">
              {row.original.lotNumber}{row.original.lotNumberExtension} - {row.original.title}
            </a>
          </>
        );
      },
    },
    {
      accessorKey: 'hammer',
      header: 'Hammer',
      accessorFn: (info) => {
        return (
        `${currencySymbol || ''}${info.hammer}`
        )},
    },
    {
      accessorKey: 'bp',
      header: 'BP',
      accessorFn: (info) => {
        return (
        `${currencySymbol || ''}${info.buyPrem}`
        )},
    },
    {
      accessorKey: 'liveFee',
      header: 'Live Fee',
      accessorFn: (info) => {
        return (
        `${currencySymbol || ''}${info.liveFee}`
        )},
    },
    {
      accessorKey: 'payable',
      header: 'Payable',
      accessorFn: (info) => {
        return (
        `${currencySymbol || ''}${info.payble}`
        )},
    },
    {
      accessorKey: 'disputeStatus',
      header: (header) => (
        <span>
          Dispute Status{' '}
          {header.header.channel?.indexOf('Floor') > -1 && (
            <Tooltip title="Disputes cannot be opened on lots won by floor bidders" placement="top" arrow>
              <span style={{ color: '#004BD6' }}>
                <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
              </span>
            </Tooltip>
          )}
        </span>
      ),
      cell: ({ row }) => {
        return (
          <a href={`${ahPath()}/ah/customers/dispute/${row.original.disputeRef}`} target = "_blank" rel="noopener noreferrer">
            {row.original.disputeStatDescription}
          </a>
        )},
    },
    {
      accessorKey: 'feedback',
      header: (header) => (
        <span>
          Feedback{' '}
          {header.header.channel?.indexOf('Floor') > -1 && (
            <Tooltip title="Feedback cannot be left for floor bidders" placement="top" arrow>
              <span style={{ color: '#004BD6' }}>
                <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
              </span>
            </Tooltip>
          )}
        </span>
      ),
      cell: ({ row }) => {
        const isPositiveFeedback = !!row.original.feedback?.positive;
        const feedbackType = isPositiveFeedback ? 'Positive' : 'Negative';

        return (
          row.original.channel?.indexOf('Floor') > -1 ? null
          : row.original.feedbackExistsForLot ? (
            <div>
            <div className="ml-2" style={{ color: isPositiveFeedback ? 'black' : 'red' }}>
                {feedbackType} feedback left
              </div>
              {canAddFeedback(row.original.catalogDate.time) && (
                <div className="ml-2">
                  <Button
                    variant="text"
                    color="secondary"
                    size="sm"
                    style={{ minWidth: 0 }}
                    onClick={() => {
                      setLoadingFeedbackModalData(true);
                      handleEditFeedback(row.original.lotRef, row.original.feedback)
                    }}
                  >
                    Edit
                  </Button>
                  <span className="mx-2">|</span>
                  <Button
                    variant="text"
                    color="secondary"
                    size="sm"
                    style={{ minWidth: 0 }}
                    onClick={() => {
                      setLoadingFeedbackModalData(true);
                      handleRemoveFeedback(row.original.lotRef, row.original.memberID)
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2">
              {canAddFeedback(row.original.catalogDate.time) && (
              <Link
                style={{
                  color: '#004BD6',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => {
                  setCurrentFeedbackLotRef(row.original.lotRef);
                  setLoadingFeedbackModalData(true);
                  setIsOpenFeedbackModal(true);
                  dispatch(fetchDefaultFeedbackMessages(houseRef, setLoadingFeedbackModalData, true));
                }}
              >
                Leave Feedback
              </Link>
              )}
            </div>
          )
        )

      }
    },
  ]
};


